import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Alert } from '@mui/material';

import BookLeadCall from '../../../../../pages/BookLeadCall';
import DialogModal from '../../../../../components/DialogModal';
import LeadConversationsContext, {
  ConversationBucket,
  ConversationChannel,
} from '../../../../context/LeadConversationsContext/LeadConversationsContext';
import { MessageFilter } from '../../../../components/Leads/components/ChatModal/utils';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import LeadModal from '../../../../components/Leads/components/v2/LeadModal/LeadModal';
import UserContext from '../../../../../context/UserContext';
import CallOutcomeModal from '../../../../components/Leads/components/CallOutcomeModal';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { leadModalTabs } from '../../../../components/Leads/components/v2/LeadModal/utils';
import leadModalTexts from '../../../../components/Leads/components/v2/LeadModal/texts';
import { returnLeadToAiSdr } from '../../../../utils/message';
import { LeadCallOutcome, SalesStage } from '../../../../Model/Lead';
import useToast from '../../../../hooks/useToast';
import LoadingPage from '../../../../../components/LoadingPage';
import {
  Container,
  LeadModalContainer,
  HeaderBar,
  FooterButtonsContainer,
} from './styles';
import texts from './texts.json';

const LeadDetailPanel = () => {
  const {
    activeLeadDoc,
    setActiveLeadId,
    conversationList,
    igConversationList,
    selectedBucket,
    isLoading,
    activeChannel,
  } = useContext(LeadConversationsContext);
  const { userDoc } = useContext(UserContext);
  const { showToast } = useToast();

  const [showMarkAsLostModal, setShowMarkAsLostModal] = useState(false);
  const [isUpdatingArchiveState, setIsUpdatingArchiveState] = useState(false);
  const [showBookCallModal, setShowBookCallModal] = useState(false);
  const [showReturnToAiSdrModal, setShowReturnToAiSdrModal] = useState(false);
  const [isReturningToAiSdr, setIsReturningToAiSdr] = useState(false);

  const isIGChannel = activeChannel === ConversationChannel.INSTAGRAM;

  const selectedLeadConversation = useMemo(() => {
    const list = isIGChannel ? igConversationList : conversationList;
    return list.find((conv) => conv.id === activeLeadDoc?.id);
  }, [conversationList, igConversationList, activeLeadDoc, isIGChannel]);

  const isConversationArchived = isIGChannel
    ? !!selectedLeadConversation?.igIsArchived
    : (selectedBucket === ConversationBucket.ARCHIVED || !!selectedLeadConversation?.data?.isArchived);

  const initialMessageFilter = isIGChannel
    ? MessageFilter.INSTAGRAM
    : MessageFilter.TEXT_EMAIL;

  const onMarkAsLostSave = useCallback(async () => {
    if (!activeLeadDoc || !userDoc?.id) {
      return;
    }

    try {
      // Archive the leadConversation doc directly in Firestore using the already-loaded doc from context
      const leadConversation = conversationList.find((conv) => conv.id === activeLeadDoc.id);
      if (leadConversation) {
        await leadConversation.archive(userDoc.id);
      }
    } catch (error) {
      showToast(error || leadModalTexts.somethingWentWrong, { error: true });
    }
  }, [conversationList, activeLeadDoc, userDoc, showToast]);

  const onArchiveUnarchiveClick = useCallback(async () => {
    if (!selectedLeadConversation || !activeLeadDoc) {
      return;
    }

    setIsUpdatingArchiveState(true);
    try {
      if (isIGChannel) {
        if (isConversationArchived) {
          await selectedLeadConversation.unarchiveIG();
        } else {
          await selectedLeadConversation.archiveIG(userDoc.id);
        }
      } else if (isConversationArchived) {
        await selectedLeadConversation.unarchive(userDoc.id);
      } else {
        await selectedLeadConversation.archive(userDoc.id);
      }
      showToast(isConversationArchived ? texts.unarchived : texts.archived);
      setActiveLeadId('');
    } catch (error) {
      showToast(error?.message || leadModalTexts.somethingWentWrong, { error: true });
    } finally {
      setIsUpdatingArchiveState(false);
    }
  }, [
    selectedLeadConversation, activeLeadDoc, isConversationArchived, isIGChannel,
    userDoc, showToast, setActiveLeadId,
  ]);

  const onReturnToAiSdr = useCallback(async () => {
    if (!activeLeadDoc || !userDoc?.id) {
      return;
    }

    setIsReturningToAiSdr(true);
    try {
      await returnLeadToAiSdr(activeLeadDoc, {
        createdBy: userDoc.id,
        createdByUserName: userDoc.name,
      });
      showToast(texts.handOverToAiSdrSuccess);
      setShowReturnToAiSdrModal(false);
    } catch (error) {
      showToast(error?.message || leadModalTexts.somethingWentWrong, { error: true });
    } finally {
      setIsReturningToAiSdr(false);
    }
  }, [activeLeadDoc, userDoc, showToast]);

  if (isLoading) {
    return (
      <Container>
        <LoadingPage />
      </Container>
    );
  }

  if (!activeLeadDoc) {
    return null;
  }

  let archiveButtonText = texts.archive;
  if (isUpdatingArchiveState) {
    archiveButtonText = texts.updating;
  } else if (isConversationArchived) {
    archiveButtonText = texts.unarchive;
  }

  return (
    <Container>
      {activeLeadDoc.useAutomatedSdr && (
        <Alert severity="error">{texts.automatedSdrBanner}</Alert>
      )}
      <LeadModalContainer>
        <HeaderBar>
          <PrimaryButton
            onClick={onArchiveUnarchiveClick}
            size="medium"
            disabled={isUpdatingArchiveState || !selectedLeadConversation}
          >
            {archiveButtonText}
          </PrimaryButton>
        </HeaderBar>
        <LeadModal
          lead={activeLeadDoc}
          isInsideSalesView
          isSDRChat
          userId={userDoc.id}
          userName={userDoc.name}
          selectedTab={leadModalTabs.CHAT}
          initialMessageFilter={initialMessageFilter}
        />
      </LeadModalContainer>
      <FooterButtonsContainer>
        <PrimaryButton onClick={() => setShowBookCallModal(true)} size="medium">
          {leadModalTexts.bookACall}
        </PrimaryButton>
        {activeLeadDoc.salesStage !== SalesStage.LOST && (
          <PrimaryButton
            onClick={() => setShowMarkAsLostModal(true)}
            size="medium"
            variant="danger"
          >
            {leadModalTexts.markAsLost}
          </PrimaryButton>
        )}
        {activeLeadDoc.isEscalatedFromAiSdr && (
          <PrimaryButton onClick={() => setShowReturnToAiSdrModal(true)} size="medium">
            {texts.handOverToAiSdr}
          </PrimaryButton>
        )}
      </FooterButtonsContainer>
      {showMarkAsLostModal && (
        <CallOutcomeModal
          lead={activeLeadDoc}
          onClose={() => setShowMarkAsLostModal(false)}
          showModal={showMarkAsLostModal}
          onCloseLeadModal={() => setShowMarkAsLostModal(false)}
          userDoc={userDoc}
          initialOutcome={LeadCallOutcome.LOST}
          isOutcomeLocked
          isSDRView
          onAfterSave={onMarkAsLostSave}
        />
      )}
      {showBookCallModal && (
        <DialogModal
          open={showBookCallModal}
          onClose={() => setShowBookCallModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <BookLeadCall
            leadId={activeLeadDoc.id}
            name={activeLeadDoc.name || ''}
            timeZone={activeLeadDoc.timezone || ''}
          />
        </DialogModal>
      )}
      {showReturnToAiSdrModal && (
        <ConfirmDialog
          isOpen={showReturnToAiSdrModal}
          confirmDisabled={isReturningToAiSdr}
          dialogTexts={{
            title: texts.handOverToAiSdrTitle,
            content: texts.handOverToAiSdrContent,
            confirm: texts.handOverToAiSdr,
            cancel: texts.cancel,
          }}
          onConfirm={onReturnToAiSdr}
          onCancel={() => setShowReturnToAiSdrModal(false)}
        />
      )}
    </Container>
  );
};

export default compose(
  observer,
)(LeadDetailPanel);
